// Centralized logging utility for debugging and monitoring

type LogLevel = "debug" | "info" | "warn" | "error";

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  context?: Record<string, any>;
  userId?: string;
  sessionId?: string;
}

class Logger {
  private sessionId: string;
  private userId?: string;
  private logBuffer: LogEntry[] = [];
  private readonly maxBufferSize = 100;
  private isDevelopment = import.meta.env.DEV;

  constructor() {
    this.sessionId = crypto.randomUUID();
  }

  setUserId(userId: string) {
    this.userId = userId;
  }

  clearUserId() {
    this.userId = undefined;
  }

  private createLogEntry(level: LogLevel, message: string, context?: Record<string, any>): LogEntry {
    return {
      timestamp: new Date().toISOString(),
      level,
      message,
      context,
      userId: this.userId,
      sessionId: this.sessionId,
    };
  }

  private addToBuffer(entry: LogEntry) {
    this.logBuffer.push(entry);

    // Keep buffer size manageable
    if (this.logBuffer.length > this.maxBufferSize) {
      this.logBuffer.shift();
    }
  }

  private formatMessage(entry: LogEntry): string {
    const emoji = {
      debug: "🔍",
      info: "ℹ️",
      warn: "⚠️",
      error: "❌",
    }[entry.level];

    let msg = `${emoji} [${entry.level.toUpperCase()}] ${entry.message}`;

    if (entry.context) {
      msg += ` | Context: ${JSON.stringify(entry.context)}`;
    }

    if (entry.userId) {
      msg += ` | User: ${entry.userId}`;
    }

    return msg;
  }

  debug(message: string, context?: Record<string, any>) {
    const entry = this.createLogEntry("debug", message, context);
    this.addToBuffer(entry);

    if (this.isDevelopment) {
      console.debug(this.formatMessage(entry));
    }
  }

  info(message: string, context?: Record<string, any>) {
    const entry = this.createLogEntry("info", message, context);
    this.addToBuffer(entry);

    console.info(this.formatMessage(entry));
  }

  warn(message: string, context?: Record<string, any>) {
    const entry = this.createLogEntry("warn", message, context);
    this.addToBuffer(entry);

    console.warn(this.formatMessage(entry));
  }

  error(message: string, error?: Error, context?: Record<string, any>) {
    const entry = this.createLogEntry("error", message, {
      ...context,
      error: error?.message,
      stack: error?.stack,
    });
    this.addToBuffer(entry);

    console.error(this.formatMessage(entry), error);
  }

  // Get recent logs for debugging
  getRecentLogs(level?: LogLevel): LogEntry[] {
    if (level) {
      return this.logBuffer.filter((entry) => entry.level === level);
    }
    return [...this.logBuffer];
  }

  // Clear log buffer
  clearLogs() {
    this.logBuffer = [];
  }

  // Export logs for support/debugging
  exportLogs(): string {
    return JSON.stringify(this.logBuffer, null, 2);
  }
}

// Singleton instance
export const logger = new Logger();

// Convenience methods for common use cases
export const logMeetingEvent = (event: string, meetingId: string, details?: Record<string, any>) => {
  logger.info(`Meeting Event: ${event}`, { meetingId, ...details });
};

export const logWebRTCEvent = (event: string, peerId?: string, details?: Record<string, any>) => {
  logger.info(`WebRTC Event: ${event}`, { peerId, ...details });
};

export const logAuthEvent = (event: string, details?: Record<string, any>) => {
  logger.info(`Auth Event: ${event}`, details);
};

export const logError = (component: string, error: Error, context?: Record<string, any>) => {
  logger.error(`Error in ${component}`, error, context);
};

// Intercept unhandled errors
window.addEventListener("error", (event) => {
  logger.error("Unhandled Error", event.error, {
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
  });
});

// Intercept unhandled promise rejections
window.addEventListener("unhandledrejection", (event) => {
  logger.error("Unhandled Promise Rejection", new Error(event.reason), {
    reason: event.reason,
  });
});
